/**
 * Asking the deployment what it does, and refusing to be vague about the answer.
 *
 * `/v1/config` decides one thing that costs a whole session when it is wrong:
 * `accepts_audio`. An explicit `false` means the deployment takes in no audio (#388): the
 * server refuses the uplink's ticket and socket, and this device does not start recording
 * at all (`recordingPaused`, checked by `session.ts` before a session is created or
 * joined). [V] The per-chunk upload and whole-recording backstop this field once gated
 * were retired with the batch lane (#242); the ticketed uplink is the only audio path now.
 * The history below (#124) is why "no answer" and "no" stay apart.
 *
 * ## Absent is not "no"
 *
 * This module keeps the two apart, the way `stream-promotion.ts` does for the promotion
 * gate: a *declined* deployment (`accepts_audio` present and not true) is a decision, and
 * an *unavailable* one is a fault. They read differently to the user and they are
 * different things for an operator to go and fix, so `audioUploadStatus` never collapses
 * them into one boolean and there is no path here that returns "maybe".
 *
 * The difference `boot.ts` used to have no way to express is why the retry lives here: one
 * lost request at boot is the most ordinary event on a phone network, and it is worth three
 * more asks over ten seconds before anybody's transcript is written off.
 */

import * as telemetry from "../../lib/telemetry.js";
import type { PublicConfigPayload } from "../contract/api";
import { t } from "../contract/strings";

/** How much boot knows about `/v1/config`. Distinct values, never a bare null. */
export type ConfigAvailability =
  /** The first attempt failed and the retry is still running. */
  | "pending"
  /** The deployment answered, and the body is in the store. */
  | "ok"
  /** Every attempt failed. Nothing downstream will get an answer this session. */
  | "unavailable";

/** One ask, resolved to a definite outcome. `config` is non-null only when `reason` is "ok". */
export interface ConfigAttempt {
  config: PublicConfigPayload | null;
  reason: "ok" | "unreachable" | "malformed";
}

/**
 * The retry schedule, as delays *after* the attempt boot already awaited.
 *
 * [D] Three retries across ~10 s. The first is a second out, because the common failure is
 * a single lost request on a handover and a second is long enough for the radio to come
 * back; the last lands at ten seconds, which is comfortably inside the model download this
 * runs beside (tens of seconds) and so costs the boot nothing. A fourth would only be
 * asking a deployment that has refused four times in ten seconds -- past that point the
 * honest thing is to say so on screen rather than keep a spinner alive.
 */
export const CONFIG_RETRY_DELAYS_MS: readonly number[] = [1000, 3000, 6000];

/** Total asks per boot, counting the one `boot.ts` awaits. Reported in telemetry. */
export const CONFIG_MAX_ATTEMPTS = CONFIG_RETRY_DELAYS_MS.length + 1;

/**
 * A `/v1/config` body turned into a definite outcome.
 *
 * A non-object body is "malformed", not an empty config: every field this app reads is
 * read with `?.`, so an array or a string would sail through as "no audio, no push, no
 * accounts" and look exactly like a deployment that had turned all three off.
 */
export function readConfigBody(body: unknown): ConfigAttempt {
  if (!body || typeof body !== "object" || Array.isArray(body)) {
    return { config: null, reason: "malformed" };
  }
  return { config: body as PublicConfigPayload, reason: "ok" };
}

/** One ask. Throws nothing: a rejected request is an outcome, not an exception. */
export async function attemptConfig(fetchConfig: () => Promise<unknown>): Promise<ConfigAttempt> {
  try {
    return readConfigBody(await fetchConfig());
  } catch {
    return { config: null, reason: "unreachable" };
  }
}

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

export interface ConfigRetryDeps {
  /** The request itself, injected so a test does not need a network or a clock. */
  fetchConfig: () => Promise<unknown>;
  /** Called with the body the moment one lands, so boot can apply it late. */
  onConfig: (config: PublicConfigPayload) => void;
  /** Called once when every attempt has failed. Boot uses it for the visible state. */
  onExhausted: (attempt: ConfigAttempt) => void;
  delays?: readonly number[];
  sleep?: (ms: number) => Promise<void>;
}

/**
 * Ask again, on the schedule above, and report either way.
 *
 * Detached from boot's critical path by its caller rather than by a flag in here: this
 * returns a promise nobody has to await, and the two callbacks are how the answer gets
 * out. The telemetry event is emitted here rather than left to the caller so that
 * "config never arrived" cannot be a thing a future caller forgets to report -- the
 * detail is scalars only, per `telemetry.js`, and the reason is the typed one from the
 * last attempt so an operator can tell a refusing server from a malformed body.
 */
export async function retryConfig(deps: ConfigRetryDeps): Promise<ConfigAttempt> {
  const delays = deps.delays ?? CONFIG_RETRY_DELAYS_MS;
  const sleep = deps.sleep ?? wait;
  let last: ConfigAttempt = { config: null, reason: "unreachable" };
  for (const [index, delay] of delays.entries()) {
    await sleep(delay);
    last = await attemptConfig(deps.fetchConfig);
    if (last.config) {
      telemetry.log("config_retry", { result: "ok", attempts: index + 2 });
      deps.onConfig(last.config);
      return last;
    }
  }
  telemetry.log("config_unavailable", {
    attempts: delays.length + 1,
    reason: last.reason,
    // A logged field, kept as it was. What stops recording is an explicit
    // `accepts_audio: false`, not a missing answer -- see `recordingPaused`.
    audio_uploads: false,
  });
  deps.onExhausted(last);
  return last;
}

/**
 * Whether this device's audio can be uploaded for transcription, and if not, why not.
 *
 * Three values because there are three situations and only one of them is a fault:
 *
 *  - `ok` -- the deployment accepts audio. Nothing to say.
 *  - `declined` -- it answered, and it does not. A deployment decision, already carried by
 *    the disclosure; worth a note mid-session and not worth alarming anybody before one.
 *  - `unknown` -- nobody could ask. Uploads are off for the same reason and the transcript
 *    is lost the same way, but this one is broken rather than chosen, and it is the only
 *    one a reload might fix.
 */
export type AudioUploadStatus = "ok" | "declined" | "unknown";

/** The slice of the store this reads, so callers need not build a whole one. */
export interface AudioUploadView {
  /** Read defensively: a body from a server that omits or malforms a field is not a decision. */
  serverConfig: Record<string, unknown> | null;
  serverConfigState?: ConfigAvailability;
}

export function audioUploadStatus(view: AudioUploadView): AudioUploadStatus {
  if (view.serverConfig) return view.serverConfig.accepts_audio === true ? "ok" : "declined";
  // Only the two states boot actually sets read as a fault. An unset field means a caller
  // that predates the availability flag entirely, and the old reading of a missing config
  // -- "this deployment does not take audio" -- is the safe one to keep for it.
  const state = view.serverConfigState;
  return state === "unavailable" || state === "pending" ? "unknown" : "declined";
}

/**
 * Whether this deployment has retired the legacy browser batch lane (#173).
 *
 * This is deliberately the opposite polarity to `audioUploadStatus` above. That function
 * treats an absent answer as a reason not to send audio, because sending audio a deployment
 * did not ask for is the harm there. Suppressing the legacy claim lane has the opposite risk:
 * an unavailable or partial config answer could silently cost a session its attribution.
 * Therefore only an explicit boolean `false` arms this gate. Absent, `null`, non-boolean,
 * `true`, and unavailable config all leave the legacy lane available.
 */
export function legacyBatchRetired(view: AudioUploadView): boolean {
  return view.serverConfig?.legacy_batch_enabled === false;
}

/**
 * Whether this deployment has turned audio off, so this device must not record (#388).
 *
 * Only an explicit boolean `false` counts, the same polarity as `legacyBatchRetired`:
 * a config that could not be fetched, or a field that is missing or malformed, is not a
 * decision to refuse, and blocking recording on it would turn one lost request at boot
 * into a device that cannot be used.
 */
export function recordingPaused(view: Pick<AudioUploadView, "serverConfig">): boolean {
  return view.serverConfig?.accepts_audio === false;
}

/** What a device says when the deployment takes no audio. Plain words, no settings names. */
export const RECORDING_PAUSED_NOTICE = t("audio.paused");

/**
 * The server's ticket refusal when it takes no audio. A wire contract with
 * `AUDIO_NOT_ACCEPTED` in `meetfolks/server/routers/stream.py`: a device that started
 * recording before the switch was turned off reads it as final and stops asking.
 */
export const AUDIO_NOT_ACCEPTED = "audio_not_accepted";

/**
 * The line `AudioNotice` shows before recording, or null for nothing to say.
 *
 * The paused deployment comes first: it is a definite answer, and it means the buttons
 * beneath are disabled. Otherwise only the unreachable-settings fault speaks
 * (`showAudioWarning`). Here rather than in the component so it can be tested in node.
 */
export function audioNoticeText(view: AudioUploadView): string | null {
  if (recordingPaused(view)) return RECORDING_PAUSED_NOTICE;
  if (!showAudioWarning(view)) return null;
  return audioUploadWarning(audioUploadStatus(view));
}

/** Whether an uplink start failed because the server takes no audio (see above). */
export function isAudioNotAccepted(error: unknown): boolean {
  const e = error as { status?: unknown; message?: unknown } | null;
  return e?.status === 403 && e?.message === AUDIO_NOT_ACCEPTED;
}

/**
 * What to put on screen *before* recording, or null when there is nothing to warn about.
 *
 * Only the fault speaks here. A deployment that takes no audio is said once, plainly, by
 * `audioNoticeText` (#388), because there recording is refused, not merely degraded.
 */
/**
 * Whether the standing banner belongs on screen. The *definite* negative only.
 *
 * Deliberately narrower than `audioUploadStatus`, which reports `"unknown"` for a boot
 * still retrying -- correct for the upload gate, because uploads really are off in that
 * window, and wrong for a banner. A returning device with the model already cached can
 * render home two seconds into boot with two asks still to come, and the sentence below
 * ends in "reload the page": advice that would abort the retry about to fix it. A warning
 * that appears during normal operation is how the one that means something gets ignored.
 *
 * Here rather than in the component so it can be tested in a node environment, which is
 * the only kind this suite has.
 */
export function showAudioWarning(view: AudioUploadView): boolean {
  if (view.serverConfigState !== "unavailable") return false;
  return audioUploadWarning(audioUploadStatus(view)) !== null;
}

export function audioUploadWarning(status: AudioUploadStatus): string | null {
  if (status !== "unknown") return null;
  return t("audio.warning");
}
